/**
 * The format checks the public forms share.
 *
 * These were written out separately in each form, and the copies had drifted:
 * ApplyQAEngineer accepted a bare domain for a resume URL, HireQATalent stripped
 * the protocol before testing a company website, and both carried their own
 * email regex. Same rules, one place, and now covered by tests.
 */

/*
 * The email pattern the forms have always used. Deliberately not Zod's built-in
 * `.email()`: that is stricter, and tightening what counts as a valid address is
 * a product decision, not a side effect of a refactor.
 */
const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export function isEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

/** A hostname with at least one dot. Protocol and `www.` are optional. */
const DOMAIN_PATTERN =
  /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

/** `example.com`, `https://www.example.com` -- but not `localhost` or `example`. */
export function isDomain(value: string): boolean {
  const cleaned = value.trim().replace(/^https?:\/\//, '').replace(/^www\./, '');
  return DOMAIN_PATTERN.test(cleaned);
}

const WEB_URL_PATTERN =
  /^(https?:\/\/)?(www\.)?([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9]))*\.[a-zA-Z]{2,}(\/[^\s]*)?$/;

/**
 * A web address, with or without a protocol, optionally with a path.
 *
 * Empty counts as valid: every field using this is optional, and requiring it is
 * the caller's job.
 */
export function isWebUrl(value: string): boolean {
  const url = value.trim();
  if (!url) return true;
  if (!WEB_URL_PATTERN.test(url)) return false;

  // The pattern accepts things the URL parser rejects, so confirm the host is
  // really a host. A parse failure is not treated as invalid: the pattern
  // already matched, and rejecting here would fail addresses that work.
  try {
    const { hostname } = new URL(url.match(/^https?:\/\//) ? url : `https://${url}`);
    return hostname.includes('.') && hostname.length > 3;
  } catch {
    return true;
  }
}

/** Any linkedin.com address. Empty counts as valid; the field is optional. */
export function isLinkedInUrl(value: string): boolean {
  const url = value.trim();
  if (!url) return true;
  return /^(https?:\/\/)?(www\.)?linkedin\.com(\/.*)?$/i.test(url);
}
